import React from "react";
import { ShipPosition } from "../types/ShipPosition";
import { useAuth, SignInButton } from "@clerk/react";

type CruiseLineFilterProps = {
  marineData: ShipPosition[] | null;
  selectedLines: string[];
  setSelectedLines: React.Dispatch<React.SetStateAction<string[]>>;
  showFilterPanel: boolean;
  setShowFilterPanel: React.Dispatch<React.SetStateAction<boolean>>;
  lineSearchTerm: string;
  setLineSearchTerm: React.Dispatch<React.SetStateAction<string>>;
};

const CruiseLineFilter: React.FC<CruiseLineFilterProps> = ({
  marineData,
  selectedLines,
  setSelectedLines,
  showFilterPanel,
  setShowFilterPanel,
  lineSearchTerm,
  setLineSearchTerm,
}) => {
  const { isSignedIn } = useAuth();

  return (
    <div className="absolute top-20 left-4 z-30">
      <div className="flex justify-end mb-2">
        {isSignedIn ? (
          <button
            onClick={() => setShowFilterPanel(!showFilterPanel)}
            className="px-4 py-2 text-sm rounded bg-gray-800 text-white border border-gray-600 hover:bg-gray-700 transition"
          >
            {showFilterPanel ? "Hide Filters" : "Filter by Line"}
          </button>
        ) : (
          <SignInButton mode="modal">
            <button className="px-4 py-2 text-sm rounded bg-gray-800/60 text-gray-400 border border-gray-600/50 flex items-center gap-2 cursor-pointer hover:bg-gray-700/60 transition">
              🔒 Filter by Line — Captain only
            </button>
          </SignInButton>
        )}
      </div>

      {isSignedIn && showFilterPanel && (
        <div className="bg-black/90 pt-0 px-3 pb-3 rounded-lg border border-gray-700 max-w-xs space-y-2 max-h-96 overflow-y-auto">
          <div className="sticky top-0 left-0 right-0 bg-black/95 z-10 px-3 py-2">
            <div className="flex flex-col space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-400">Select Cruise Lines</span>
                <div className="flex gap-2">
                  <button
                    onClick={() =>
                      setSelectedLines(
                        Array.from(
                          new Set(
                            marineData?.map((s) => s.LINE).filter(Boolean) || []
                          )
                        )
                      )
                    }
                    className="text-xs text-green-400 hover:underline"
                  >
                    Select All
                  </button>
                  <button
                    onClick={() => setSelectedLines([])}
                    className="text-xs text-green-400 hover:underline"
                  >
                    Unselect All
                  </button>
                </div>
              </div>
              <input
                type="text"
                placeholder="Search cruise lines..."
                value={lineSearchTerm}
                onChange={(e) => setLineSearchTerm(e.target.value)}
                className="w-full px-2 py-1 text-sm rounded bg-gray-800 text-white border border-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {Array.from(
              new Set(marineData?.map((s) => s.LINE).filter(Boolean) || [])
            )
              .filter((line) =>
                line.toLowerCase().includes(lineSearchTerm.toLowerCase())
              )
              .map((line) => (
                <button
                  key={line}
                  onClick={() =>
                    setSelectedLines((prev) =>
                      prev.includes(line)
                        ? prev.filter((l) => l !== line)
                        : [...prev, line]
                    )
                  }
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-all duration-150 ${
                    selectedLines.includes(line)
                      ? "bg-green-500 text-white border-green-400"
                      : "bg-black border-gray-600 text-gray-300 hover:bg-gray-800"
                  }`}
                >
                  {line}
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CruiseLineFilter;
